import axios, { AxiosError } from 'axios';
import {
  User,
  AuthResponse,
  LoginCredentials,
  SignupCredentials,
  ResetPasswordData,
  ApiError,
} from '../types';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4011';

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to include auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Add response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError<ApiError>) => {
    if (error.response?.data) {
      throw error.response.data;
    }
    throw { message: 'Network error. Please try again.' };
  },
);

export const authService = {
  /**
   * Sign up a new user
   */
  async signup(credentials: SignupCredentials): Promise<AuthResponse> {
    const { data } = await apiClient.post<AuthResponse>(
      '/auth/signup',
      credentials,
    );
    return data;
  },

  /**
   * Log in with email and password
   */
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    const { data } = await apiClient.post<AuthResponse>(
      '/auth/login',
      credentials,
    );
    return data;
  },

  /**
   * Log out current user
   */
  async logout(): Promise<{ message: string }> {
    const { data } = await apiClient.post('/auth/logout');
    return data;
  },

  /**
   * Refresh access token using refresh token
   */
  async refreshToken(refreshToken: string): Promise<{ accessToken: string }> {
    const { data } = await apiClient.post('/auth/refresh', { refreshToken });
    return data;
  },

  /**
   * Verify email with token
   */
  async verifyEmail(token: string): Promise<{ message: string }> {
    const { data } = await apiClient.post('/auth/verify-email', { token });
    return data;
  },

  /**
   * Request password reset email
   */
  async forgotPassword(email: string): Promise<{ message: string }> {
    const { data } = await apiClient.post('/auth/forgot-password', { email });
    return data;
  },

  /**
   * Reset password with token
   */
  async resetPassword(
    resetData: ResetPasswordData,
  ): Promise<{ message: string }> {
    const { data } = await apiClient.post('/auth/reset-password', resetData);
    return data;
  },

  /**
   * Get current user profile
   */
  async getCurrentUser(): Promise<User> {
    const { data } = await apiClient.get<User>('/auth/me');
    return data;
  },
};

export default authService;
